/**
 * Shared onboarding option config — single source of truth for BOTH onboarding
 * flows (Flow A auth-modal `StyleStep`, Flow B results-wall `ResultsOnboarding`)
 * AND for the brain's shopper-memory block, which reads a signed-in shopper's
 * saved ids back out of `users.metadata.onboarding_data` (DUP-2406).
 *
 * ⛔ It lives in `@dupe/common` for that third consumer: `apps/api` cannot import
 * from `apps/web`, and a duplicated id->label map in the api would drift silently
 * the first time a tile is renamed — the shopper would then be described to the
 * model by a label no longer shown to them at signup. `apps/web` re-exports this
 * module from its original path so the web consumers are unchanged.
 *
 * Every option set here is grounded in a three-source behavioral study of the
 * active-user tail (Postgres + PostHog + Tinybird). Two findings shape the
 * design and must not be undone by a well-meaning "cleaner" copy edit:
 *
 *  1. Users do NOT name their aesthetic (<1% type a style word) — they reveal it
 *     through the BRAND they paste. So every style tile is *brand-anchored*:
 *     `brands` renders as an example-chip subtitle. That is the load-bearing
 *     part of the tile, not decoration. Order = real prevalence rank.
 *  2. A third of brand-signal users paste style-neutral marketplaces
 *     (Wayfair/Amazon) — so a "show me everything" escape hatch is REQUIRED, not
 *     optional. It is `NOT_SURE_ID` and is appended to each style group.
 *
 * Budget is intentionally NOT asked — it is encoded in the source brand
 * (RH = premium, West Elm = mid, IKEA/Wayfair = value) and inferred downstream.
 *
 * See docs/features/active/onboarding-personas/ for the full study + rationale.
 */

/** A brand-anchored style tile. `brands` is the example-chip subtitle. */
export interface StyleOption {
  id: string
  label: string
  /** Example brands shown as a subtitle — the "brand-anchored" signal. */
  brands: readonly string[]
}

/** Deduced shopper gender, as stored by the brand-led onboarding arm. */
export type OnboardingGender = 'womens' | 'mens' | 'both' | 'unknown'

/** A plain labelled option (department, shopping-for, materials). */
export interface LabeledOption {
  id: string
  label: string
}

/** The escape-hatch tile id, shared by both style groups. */
export const NOT_SURE_ID = 'not-sure'

const NOT_SURE_HOME: StyleOption = {
  id: NOT_SURE_ID,
  label: 'Not sure — show me everything',
  brands: [],
}
const NOT_SURE_FASHION: StyleOption = {
  id: NOT_SURE_ID,
  label: 'Not sure — show me everything',
  brands: [],
}

/** Top-level category gate. Reused from Flow B's original `SHOP_FOR`. */
export const CATEGORY_OPTIONS: readonly LabeledOption[] = [
  { id: 'home', label: 'Home' },
  { id: 'fashion', label: 'Fashion' },
  { id: 'both', label: 'A bit of both' },
] as const

export type CategoryId = 'home' | 'fashion' | 'both'

/**
 * HOME styles — shown when category ∈ {home, both}. Multi-select (pick 1–2).
 * Order = prevalence rank from the study (largest first). Coastal + Farmhouse
 * are the smaller optional tail; kept because they are distinct enough that a
 * shopper in them would not find themselves in the top six.
 */
export const HOME_STYLES: readonly StyleOption[] = [
  {
    id: 'modern-minimal',
    label: 'Modern & minimal',
    brands: ['IKEA', 'CB2', 'Crate & Barrel'],
  },
  {
    id: 'mid-century',
    label: 'Mid-century modern',
    brands: ['West Elm', 'Article'],
  },
  {
    id: 'traditional',
    label: 'Traditional & classic',
    brands: ['Pottery Barn', 'Ballard Designs'],
  },
  {
    id: 'boho',
    label: 'Boho & eclectic',
    brands: ['Anthropologie', 'Urban Outfitters'],
  },
  {
    id: 'organic-modern',
    label: 'Warm & organic modern',
    brands: ['McGee & Co', 'Arhaus'],
  },
  {
    id: 'glam',
    label: 'Glam & luxe',
    brands: ['RH', 'Perigold'],
  },
  {
    id: 'coastal',
    label: 'Coastal',
    brands: ['Serena & Lily'],
  },
  {
    id: 'farmhouse',
    label: 'Farmhouse & rustic',
    brands: ['Magnolia', 'Birch Lane'],
  },
  NOT_SURE_HOME,
] as const

/**
 * FASHION styles — shown when category ∈ {fashion, both}. Multi-select.
 * Order = prevalence rank. `kfashion-streetwear` is the international wedge
 * (KR/JP) — small by volume but a distinct base worth its own tile.
 */
export const FASHION_STYLES: readonly StyleOption[] = [
  {
    id: 'elevated-minimal',
    label: 'Elevated & minimal',
    brands: ['Aritzia', 'Reformation', 'Skims'],
  },
  {
    id: 'trendy-going-out',
    label: 'Trendy & going-out',
    brands: ['Zara', 'Revolve', 'Princess Polly'],
  },
  {
    id: 'designer-for-less',
    label: 'Designer for less',
    brands: ['Coach', 'Gucci', 'Louis Vuitton'],
  },
  {
    id: 'athleisure',
    label: 'Active & athleisure',
    brands: ['Lululemon', 'Alo', 'Nike'],
  },
  {
    id: 'preppy',
    label: 'Preppy & classic',
    brands: ['Abercrombie', 'J.Crew'],
  },
  {
    id: 'boho-feminine',
    label: 'Boho & feminine',
    brands: ['Free People', 'Anthropologie'],
  },
  {
    id: 'kfashion-streetwear',
    label: 'K-fashion & streetwear',
    brands: ['Musinsa', 'Zigzag'],
  },
  NOT_SURE_FASHION,
] as const

/**
 * MENSWEAR fashion styles — shown in step 3 when the shopper's brand picks lean
 * male (deduced from `genderLean`). Two are menswear-specific (`tailored-
 * smartcasual`, `rugged-workwear`) replacing the women's-only `boho-feminine` /
 * `trendy-going-out`; the rest re-use the shared style ids with menswear imagery
 * (resolved via the `-m-1` card variant). Order = male relevance; step 3 shows
 * the top 4 (elevated-minimal, tailored-smartcasual, rugged-workwear,
 * streetwear).
 */
export const MENS_FASHION_STYLES: readonly StyleOption[] = [
  {
    id: 'elevated-minimal',
    label: 'Elevated & minimal',
    brands: ['Aimé Leon Dore', 'COS', 'Uniqlo'],
  },
  {
    id: 'tailored-smartcasual',
    label: 'Tailored & smart-casual',
    brands: ['Todd Snyder', 'Ralph Lauren', 'Aimé Leon Dore'],
  },
  {
    id: 'rugged-workwear',
    label: 'Rugged & workwear',
    brands: ['Buck Mason', 'Faherty', 'Carhartt'],
  },
  {
    id: 'kfashion-streetwear',
    label: 'Streetwear',
    brands: ['Kith', 'Aimé Leon Dore', 'Stüssy'],
  },
  {
    id: 'athleisure',
    label: 'Active & athleisure',
    brands: ['Vuori', 'Nike', 'Lululemon'],
  },
  {
    id: 'preppy',
    label: 'Preppy & classic',
    brands: ['J.Crew', 'Ralph Lauren', 'Abercrombie'],
  },
  {
    id: 'designer-for-less',
    label: 'Designer for less',
    brands: ['Coach', 'Gucci', 'Saint Laurent'],
  },
  NOT_SURE_FASHION,
] as const

/**
 * "Who are you shopping for?" — shown when category ∈ {fashion, both}.
 * Multi-select. Order + default from a measured 90-day split of fashion
 * activity: Women ≈ 87% · Men ≈ 9–10% · Kids ≈ 2%. `everyone` is the DEFAULT
 * selection (do NOT pre-select `womens` — it misfires for ~1 in 8 shoppers, and
 * ~48% of fashion brand traffic carries no department signal at all).
 */
export const FASHION_DEPARTMENT: readonly LabeledOption[] = [
  { id: 'womens', label: "Women's" },
  { id: 'mens', label: "Men's" },
  { id: 'kids', label: "Kids'" },
  { id: 'everyone', label: 'Everyone' },
] as const

/** Default department selection — see note above. */
export const FASHION_DEPARTMENT_DEFAULT: readonly string[] = ['everyone']

/**
 * "What are you shopping for?" (denser signal than vibe). Branch on category.
 * Home: sofa + bedroom dominate the study. Fashion: dresses lead (~20%).
 */
export const SHOPPING_FOR_HOME: readonly LabeledOption[] = [
  { id: 'sofa-living', label: 'Sofa & living room' },
  { id: 'bedroom', label: 'Bedroom' },
  { id: 'dining', label: 'Dining' },
  { id: 'office', label: 'Office' },
  { id: 'outdoor', label: 'Outdoor' },
] as const

export const SHOPPING_FOR_FASHION: readonly LabeledOption[] = [
  { id: 'dresses', label: 'Dresses' },
  { id: 'tops', label: 'Tops' },
  { id: 'bags', label: 'Bags' },
  { id: 'shoes', label: 'Shoes' },
  { id: 'outerwear', label: 'Outerwear' },
  { id: 'denim', label: 'Denim' },
  { id: 'accessories', label: 'Accessories' },
] as const

/** Default shopping-for selection for the fashion branch (~20% lead). */
export const SHOPPING_FOR_FASHION_DEFAULT: readonly string[] = ['dresses']

/** Materials — optional Q3, home only. Multi-select. */
export const MATERIALS: readonly LabeledOption[] = [
  { id: 'wood', label: 'Wood' },
  { id: 'leather', label: 'Leather' },
  { id: 'velvet', label: 'Velvet' },
  { id: 'boucle', label: 'Bouclé' },
  { id: 'marble-stone', label: 'Marble & stone' },
  { id: 'rattan-natural', label: 'Rattan & natural' },
] as const

/** True when a category selection should surface the HOME style group. */
export const showsHome = (category: CategoryId | null): boolean =>
  category === 'home' || category === 'both'

/** True when a category selection should surface the FASHION style/department. */
export const showsFashion = (category: CategoryId | null): boolean =>
  category === 'fashion' || category === 'both'

// ── Reading a saved profile back out (DUP-2406) ──────────────────────────────
//
// The onboarding flows WRITE ids. The brain READS them, to describe a shopper to
// the model in its shopper-memory block. Everything below is that read path.

/** Every style tile, both categories, menswear included. Ids repeat across the
 *  groups by design (`elevated-minimal` is in FASHION_STYLES and
 *  MENS_FASHION_STYLES with different example brands) — first write wins, which
 *  keeps the womenswear brands for shared ids. The brands are illustrative
 *  either way; the label is what carries the meaning. */
const byId = (
  options: readonly StyleOption[]
): ReadonlyMap<string, StyleOption> =>
  new Map(
    options
      .filter((o) => o.id !== NOT_SURE_ID)
      .map((o) => [o.id, o] as const)
      .reverse() // first occurrence wins; Map takes the last write
  )

/** Default lookup. Womenswear wins for ids shared with the menswear grid — the
 *  right prior, since ~87% of fashion activity is womenswear. */
const STYLE_BY_ID = byId([
  ...HOME_STYLES,
  ...FASHION_STYLES,
  ...MENS_FASHION_STYLES,
])

/** ⛔ MENSWEAR NEEDS ITS OWN LOOKUP, and this is not a nicety.
 *
 *  Five ids appear in BOTH fashion grids with different labels and different
 *  example brands. The onboarding flow stores the shared id — it strips the `-m`
 *  tile suffix before saving (`brandOnboardingConfig.ts`, `selectionsToMetadata`)
 *  — so by the time we read it back, a man who tapped **Streetwear (Kith, Aimé
 *  Leon Dore, Stüssy)** is indistinguishable from a woman who tapped
 *  **K-fashion & streetwear (Musinsa, Zigzag)**.
 *
 *  Resolving that against the womenswear map hands the model a different
 *  aesthetic and a set of Korean retailers he never chose — and the memory block
 *  explicitly tells the model to use these words when it searches. That is not a
 *  cosmetic mislabel; it is a wrong search. Found by the #5115 adversarial review,
 *  reproduced through the real onboarding round trip. */
const MENS_STYLE_BY_ID = byId([...HOME_STYLES, ...MENS_FASHION_STYLES])

const LABEL_BY_ID: ReadonlyMap<string, string> = new Map(
  [
    ...CATEGORY_OPTIONS,
    ...FASHION_DEPARTMENT,
    ...SHOPPING_FOR_HOME,
    ...SHOPPING_FOR_FASHION,
    ...MATERIALS,
  ].map((o) => [o.id, o.label] as const)
)

/**
 * Human label for a style id, plus its example brands.
 *
 * ⚠️ Returns `null` for `not-sure`, and that is the point rather than an
 * oversight. `not-sure` is a REQUIRED answer chosen by roughly a third of
 * brand-signal shoppers (see the header note) — it means "show me everything",
 * so rendering it into the prompt as `Style: not sure` would hand the model a
 * preference the shopper explicitly declined to express. Unknown ids also return
 * `null`: an id can outlive a tile rename, and a raw slug in the prompt is worse
 * than silence.
 */
export function describeStyleId(
  id: string,
  gender?: OnboardingGender
): StyleOption | null {
  if (id === NOT_SURE_ID) return null
  // ⚠️ Only an explicit `mens` switches grids. `both` and `unknown` keep the
  // womenswear default — `unknown` is ~63% of the field, so treating it as a
  // menswear signal would mislabel far more shoppers than it fixes.
  if (gender === 'mens') {
    const mens = MENS_STYLE_BY_ID.get(id)
    if (mens) return mens
  }
  return STYLE_BY_ID.get(id) ?? null
}

/** Human label for a non-style option id (category, department, shopping-for,
 *  material). `null` for anything unrecognised, same reasoning as above. */
export function describeOptionId(id: string): string | null {
  return LABEL_BY_ID.get(id) ?? null
}

/** Style ids -> `"Mid-century modern (West Elm, Article)"`, skipping `not-sure`
 *  and unknown ids. The example brands ride along because they are the
 *  load-bearing signal (header note 1): shoppers reveal taste through brands,
 *  not adjectives, so "Boho & eclectic" alone is far weaker steer for a product
 *  search than the same label next to Anthropologie and Urban Outfitters. */
export function describeStyleIds(
  ids: readonly string[] | undefined,
  opts: { gender?: OnboardingGender; withBrands?: boolean } = {}
): string[] {
  if (!ids?.length) return []
  const seen: StyleOption[] = []
  for (const id of ids) {
    const option = describeStyleId(id, opts.gender)
    if (!option || seen.some((o) => o.id === option.id)) continue
    seen.push(option)
  }
  // ⛔ `withBrands` IS THE CALLER'S DECISION, NOT THIS FUNCTION'S, and that is the
  // whole point of the parameter.
  //
  // The rule is "a broad taste loses its example brands" — one style plus its
  // brands is a sharp steer, many styles plus a dozen retailers is a wall of
  // conflicting shops that reads as a shopping list. But "broad" is a property of
  // the RENDERED LINE, and the line is built from two calls (home and fashion).
  // Deciding it in here counted each category alone, so a shopper with 2 home and
  // 2 fashion picks got four styles and up to ten retailers on one line — the
  // exact wall the cap exists to prevent, on ~5% of profiles. Found by the #5115
  // review. The caller counts the whole line and tells us.
  const withBrands = opts.withBrands ?? seen.length <= 2
  return seen.map((option) =>
    withBrands && option.brands.length > 0
      ? `${option.label} (${option.brands.join(', ')})`
      : option.label
  )
}

/** How many distinct, describable styles a set of ids amounts to — what the
 *  caller counts across every category before deciding whether to show brands. */
export function countStyleIds(
  ids: readonly string[] | undefined,
  gender?: OnboardingGender
): number {
  if (!ids?.length) return 0
  const seen = new Set<string>()
  for (const id of ids) {
    const option = describeStyleId(id, gender)
    if (option) seen.add(option.id)
  }
  return seen.size
}

/** At most this many styles on one line keep their example brands. */
export const STYLE_BRANDS_LIMIT = 2

/** Option ids -> labels, skipping unknowns. */
export function describeOptionIds(
  ids: readonly string[] | undefined
): string[] {
  if (!ids?.length) return []
  const out: string[] = []
  for (const id of ids) {
    const label = describeOptionId(id)
    if (label) out.push(label)
  }
  return out
}
